import type { FamilyId, ObjectiveType } from '../domains/lp-generation/schema';
import type { SiteDocumentMetadata, SitePage, SiteSection } from '../domains/site/schema';

export type GeneratedSiteFooterVariant = 'minimal' | 'compact' | 'site';
type GeneratedSiteFooterLegalLinkKey = 'company' | 'terms' | 'privacy';
type GeneratedSiteFooterSectionLinkCandidate = {
  label: string;
  sectionTypes: SiteSection['type'][];
};
export type GeneratedSiteFooterLink = {
  label: string;
  href: string;
  current?: boolean;
};
export type GeneratedSiteFooterProfile = {
  variant: GeneratedSiteFooterVariant;
  description: string;
  pageColumnTitle: string;
  legalColumnTitle: string;
  supportColumnTitle: string;
  supportFallbackMessage: string;
  contactLabel: string;
  legalLinkOrder: GeneratedSiteFooterLegalLinkKey[];
  sectionLinkCandidates: GeneratedSiteFooterSectionLinkCandidate[];
};

const SITE_FOOTER_FAMILIES = new Set<FamilyId>([
  'btob_inquiry',
  'saas_ai_inquiry',
  'seminar_application',
  'education_application',
  'recruitment_entry',
]);

const COMPACT_FOOTER_FAMILIES = new Set<FamilyId>([
  'regional_service_inquiry',
  'store_reservation',
  'd2c_purchase',
]);

const DEFAULT_SITE_SECTION_LINKS: GeneratedSiteFooterSectionLinkCandidate[] = [
  { label: '料金', sectionTypes: ['pricing'] },
  { label: 'よくある質問', sectionTypes: ['faq'] },
];

const DEFAULT_COMPACT_SECTION_LINKS: GeneratedSiteFooterSectionLinkCandidate[] = [
  { label: 'よくある質問', sectionTypes: ['faq'] },
];

const FAMILY_SECTION_LINKS: Partial<Record<FamilyId, GeneratedSiteFooterSectionLinkCandidate[]>> = {
  btob_inquiry: [
    { label: '対応内容', sectionTypes: ['services'] },
    { label: '比較', sectionTypes: ['comparison'] },
    { label: '導入事例', sectionTypes: ['use_cases', 'proof'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  saas_ai_inquiry: [
    { label: '機能', sectionTypes: ['feature-list', 'feature_list', 'features'] },
    { label: '導入事例', sectionTypes: ['use_cases', 'review', 'proof'] },
    { label: '比較', sectionTypes: ['comparison'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  regional_service_inquiry: [
    { label: '対応内容', sectionTypes: ['services'] },
    { label: 'アクセス', sectionTypes: ['access'] },
    { label: '口コミ', sectionTypes: ['review'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  store_reservation: [
    { label: 'メニュー', sectionTypes: ['services'] },
    { label: '料金', sectionTypes: ['pricing'] },
    { label: 'アクセス', sectionTypes: ['access'] },
    { label: '口コミ', sectionTypes: ['review'] },
  ],
  d2c_purchase: [
    { label: '特徴', sectionTypes: ['feature-list', 'features', 'benefit', 'feature_list'] },
    { label: 'レビュー', sectionTypes: ['review'] },
    { label: '料金', sectionTypes: ['pricing'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  seminar_application: [
    { label: '開催概要', sectionTypes: ['overview'] },
    { label: '登壇者', sectionTypes: ['speaker'] },
    { label: '特典', sectionTypes: ['offer'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  education_application: [
    { label: '講座内容', sectionTypes: ['course_or_curriculum'] },
    { label: '講師', sectionTypes: ['instructor'] },
    { label: '料金', sectionTypes: ['pricing'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
  recruitment_entry: [
    { label: '働き方', sectionTypes: ['work_style'] },
    { label: '人・チーム', sectionTypes: ['people'] },
    { label: 'メッセージ', sectionTypes: ['message'] },
    { label: 'よくある質問', sectionTypes: ['faq'] },
  ],
};

function inferObjectiveFromGoal(goal: string): ObjectiveType | null {
  if (/応募|採用|エントリー/u.test(goal)) return 'recruitment';
  if (/資料請求/u.test(goal)) return 'document_request';
  if (/予約/u.test(goal)) return 'reservation';
  if (/購入|注文/u.test(goal)) return 'purchase';
  if (/申込|申し込み|申し込/u.test(goal)) return 'application';
  if (/問い合わせ|お問合せ|相談/u.test(goal)) return 'inquiry';
  return null;
}

function resolveFamilyId(metadata: SiteDocumentMetadata): FamilyId | null {
  return metadata.pipelineContext?.pageComposition?.selectedFamilyId ?? null;
}

function resolveObjective(metadata: SiteDocumentMetadata): ObjectiveType | null {
  return metadata.pipelineContext?.classification?.objective.value
    ?? metadata.pipelineContext?.pageComposition?.classification?.objective.value
    ?? inferObjectiveFromGoal(metadata.goal);
}

export function resolveGeneratedSiteFooterVariant(metadata: SiteDocumentMetadata): GeneratedSiteFooterVariant {
  const familyId = resolveFamilyId(metadata);

  if (familyId && SITE_FOOTER_FAMILIES.has(familyId)) {
    return 'site';
  }

  if (familyId && COMPACT_FOOTER_FAMILIES.has(familyId)) {
    return 'compact';
  }

  const objective = resolveObjective(metadata);
  if (objective === 'purchase' || objective === 'reservation') {
    return 'compact';
  }

  if (
    objective === 'inquiry'
    || objective === 'document_request'
    || objective === 'application'
    || objective === 'recruitment'
  ) {
    return 'site';
  }

  return 'minimal';
}

function inferGeneratedSiteFooterContactLabel(metadata: SiteDocumentMetadata): string {
  const objective = resolveObjective(metadata);

  if (objective === 'reservation') return '予約フォーム';
  if (objective === 'purchase') return '購入相談';
  if (objective === 'application') return 'お申し込み';
  if (objective === 'recruitment') return 'エントリー';
  if (objective === 'document_request') return '資料請求';

  return 'お問い合わせ';
}

function inferGeneratedSiteFooterDescription(
  metadata: SiteDocumentMetadata,
  variant = resolveGeneratedSiteFooterVariant(metadata),
): string {
  const objective = resolveObjective(metadata);

  if (variant === 'compact') {
    if (objective === 'reservation') return '予約前に確認したい情報をコンパクトにまとめています。';
    if (objective === 'purchase') return '購入前に確認したい情報をコンパクトにまとめています。';
    return '検討前に確認したい情報をコンパクトにまとめています。';
  }

  if (variant === 'site') {
    if (objective === 'recruitment') return '応募前に知っておきたい情報を整理しています。';
    if (objective === 'application') return 'お申し込み前に必要な情報を整理しています。';
    if (objective === 'document_request') return '資料請求前に必要な情報を整理しています。';
    return 'お問い合わせ前に必要な情報を整理しています。';
  }

  return '必要な情報と法務導線をシンプルにまとめています。';
}

export function resolveGeneratedSiteFooterProfile(metadata: SiteDocumentMetadata): GeneratedSiteFooterProfile {
  const variant = resolveGeneratedSiteFooterVariant(metadata);
  const familyId = resolveFamilyId(metadata);
  const contactLabel = inferGeneratedSiteFooterContactLabel(metadata);
  const description = inferGeneratedSiteFooterDescription(metadata, variant);
  const defaultLegalLinkOrder: GeneratedSiteFooterLegalLinkKey[] =
    variant === 'site' ? ['company', 'terms', 'privacy'] : ['privacy', 'terms', 'company'];
  const defaultSectionLinkCandidates =
    (familyId ? FAMILY_SECTION_LINKS[familyId] : undefined) ?? (variant === 'site' ? DEFAULT_SITE_SECTION_LINKS : DEFAULT_COMPACT_SECTION_LINKS);

  if (familyId === 'btob_inquiry') {
    return {
      variant,
      description: 'お問い合わせ前に必要な情報を整理しています。',
      pageColumnTitle: '導入情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '相談窓口',
      supportFallbackMessage: '導入相談や個別の確認はお問い合わせから承っています。',
      contactLabel,
      legalLinkOrder: ['company', 'terms', 'privacy'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.btob_inquiry ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'saas_ai_inquiry') {
    return {
      variant,
      description: '導入検討に必要な情報を整理しています。',
      pageColumnTitle: '製品情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '導入相談',
      supportFallbackMessage: '導入相談やデモの確認はお問い合わせから承っています。',
      contactLabel,
      legalLinkOrder: ['company', 'terms', 'privacy'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.saas_ai_inquiry ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'seminar_application') {
    return {
      variant,
      description: '参加前に必要な情報を整理しています。',
      pageColumnTitle: '開催情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '参加サポート',
      supportFallbackMessage: '参加条件やご質問はお申し込み前にご確認いただけます。',
      contactLabel,
      legalLinkOrder: ['privacy', 'terms', 'company'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.seminar_application ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'education_application') {
    return {
      variant,
      description: '受講前に必要な情報を整理しています。',
      pageColumnTitle: '講座情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '受講サポート',
      supportFallbackMessage: '受講条件や学習相談はお申し込み前にご確認いただけます。',
      contactLabel,
      legalLinkOrder: ['privacy', 'terms', 'company'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.education_application ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'recruitment_entry') {
    return {
      variant,
      description: '応募前に知っておきたい情報を整理しています。',
      pageColumnTitle: '採用情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '採用窓口',
      supportFallbackMessage: '募集条件や選考についての確認はエントリーから承っています。',
      contactLabel,
      legalLinkOrder: ['company', 'privacy', 'terms'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.recruitment_entry ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'store_reservation') {
    return {
      variant,
      description: '来店前に確認したい情報をコンパクトにまとめています。',
      pageColumnTitle: '店舗情報',
      legalColumnTitle: 'ご利用案内',
      supportColumnTitle: '予約',
      supportFallbackMessage: '空き状況や来店前の確認は予約フォームから承っています。',
      contactLabel,
      legalLinkOrder: ['privacy', 'terms', 'company'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.store_reservation ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'd2c_purchase') {
    return {
      variant,
      description: '購入前に確認したい情報をコンパクトにまとめています。',
      pageColumnTitle: '商品情報',
      legalColumnTitle: 'ご購入案内',
      supportColumnTitle: '購入サポート',
      supportFallbackMessage: '購入前のご相談や確認は購入サポートから承っています。',
      contactLabel,
      legalLinkOrder: ['privacy', 'terms', 'company'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.d2c_purchase ?? defaultSectionLinkCandidates,
    };
  }

  if (familyId === 'regional_service_inquiry') {
    return {
      variant,
      description: '相談前に確認したい情報をコンパクトにまとめています。',
      pageColumnTitle: 'サービス情報',
      legalColumnTitle: '法務',
      supportColumnTitle: '相談窓口',
      supportFallbackMessage: 'ご相談前の確認はお問い合わせから承っています。',
      contactLabel,
      legalLinkOrder: ['privacy', 'terms', 'company'],
      sectionLinkCandidates: FAMILY_SECTION_LINKS.regional_service_inquiry ?? defaultSectionLinkCandidates,
    };
  }

  return {
    variant,
    description,
    pageColumnTitle: 'ページ',
    legalColumnTitle: '法務',
    supportColumnTitle: variant === 'site' ? 'サポート' : 'お問い合わせ',
    supportFallbackMessage:
      variant === 'site'
        ? '必要な確認はお問い合わせから承っています。'
        : '必要な確認は各種お問い合わせ窓口から承っています。',
    contactLabel,
    legalLinkOrder: defaultLegalLinkOrder,
    sectionLinkCandidates: defaultSectionLinkCandidates,
  };
}

export function getGeneratedSiteFooterContactLabel(metadata: SiteDocumentMetadata): string {
  return resolveGeneratedSiteFooterProfile(metadata).contactLabel;
}

export function getGeneratedSiteFooterDescription(
  metadata: SiteDocumentMetadata,
  variant = resolveGeneratedSiteFooterVariant(metadata),
): string {
  const profile = resolveGeneratedSiteFooterProfile(metadata);
  return profile.variant === variant ? profile.description : inferGeneratedSiteFooterDescription(metadata, variant);
}

export function buildGeneratedSiteSectionAnchorId(sectionType: SiteSection['type'], occurrence = 1): string {
  return occurrence === 1 ? `section-${sectionType}` : `section-${sectionType}-${occurrence}`;
}

export function buildGeneratedSiteSectionAnchors(page: SitePage) {
  const occurrences = new Map<SiteSection['type'], number>();

  return page.sections.map((section) => {
    const nextOccurrence = (occurrences.get(section.type) ?? 0) + 1;
    occurrences.set(section.type, nextOccurrence);

    return {
      sectionId: section.id,
      sectionType: section.type,
      anchorId: buildGeneratedSiteSectionAnchorId(section.type, nextOccurrence),
    };
  });
}

export function buildGeneratedSiteFooterLegalLinks(
  metadata: SiteDocumentMetadata,
  privacyHref?: string,
): GeneratedSiteFooterLink[] {
  const profile = resolveGeneratedSiteFooterProfile(metadata);
  const linkMap: Record<GeneratedSiteFooterLegalLinkKey, GeneratedSiteFooterLink> = {
    company: { label: '運営会社', href: '/company' },
    terms: { label: '利用規約', href: '/terms' },
    privacy: { label: 'プライバシーポリシー', href: privacyHref || '/privacy' },
  };

  return profile.legalLinkOrder.map((key) => linkMap[key]);
}

export function buildGeneratedSiteFooterSupportLinks(
  metadata: SiteDocumentMetadata,
  page: SitePage,
  contactHref?: string,
): GeneratedSiteFooterLink[] {
  const profile = resolveGeneratedSiteFooterProfile(metadata);
  const anchors = buildGeneratedSiteSectionAnchors(page);
  const firstAnchorByType = new Map<SiteSection['type'], string>();

  anchors.forEach((anchor) => {
    if (!firstAnchorByType.has(anchor.sectionType)) {
      firstAnchorByType.set(anchor.sectionType, `#${anchor.anchorId}`);
    }
  });

  const links = profile.sectionLinkCandidates.flatMap((candidate) => {
    const href = candidate.sectionTypes
      .map((sectionType) => firstAnchorByType.get(sectionType))
      .find((value): value is string => Boolean(value));

    return href ? [{ label: candidate.label, href }] : [];
  });

  if (contactHref) {
    links.push({ label: profile.contactLabel, href: contactHref });
  }

  return links;
}
